(function () {
  'use strict';

  var MAX_ROOM_BYTES = 20 * 1024 * 1024;
  var MAX_ROOM_EDGE = 1800;
  var MAX_PRODUCT_EDGE = 1400;
  var MAX_ROOM_ITEMS = 12;
  var HISTORY_LIMIT = 35;

  function clamp(value, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value));
  }

  function imageDimensions(image) {
    return {
      width: image.naturalWidth || image.width,
      height: image.naturalHeight || image.height
    };
  }

  function loadImage(source, crossOrigin) {
    return new Promise(function (resolve, reject) {
      var image = new Image();
      if (crossOrigin) image.crossOrigin = 'anonymous';
      image.onload = function () { resolve(image); };
      image.onerror = function () { reject(new Error('Image could not be loaded.')); };
      image.src = source;
    });
  }

  function scaledCanvasForImage(image, maximumEdge) {
    var dimensions = imageDimensions(image);
    var ratio = Math.min(1, maximumEdge / Math.max(dimensions.width, dimensions.height));
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(dimensions.width * ratio));
    canvas.height = Math.max(1, Math.round(dimensions.height * ratio));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas;
  }

  function cleanConnectedWhiteBackground(image) {
    var canvas = scaledCanvasForImage(image, MAX_PRODUCT_EDGE);
    var context = canvas.getContext('2d', { willReadFrequently: true });
    var imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    var data = imageData.data;
    var width = canvas.width;
    var height = canvas.height;
    var count = width * height;
    var visited = new Uint8Array(count);
    var queue = new Int32Array(count);
    var head = 0;
    var tail = 0;

    function isBackground(index) {
      var offset = index * 4;
      var alpha = data[offset + 3];
      if (alpha < 18) return true;
      var red = data[offset];
      var green = data[offset + 1];
      var blue = data[offset + 2];
      var maximum = Math.max(red, green, blue);
      var minimum = Math.min(red, green, blue);
      var average = (red + green + blue) / 3;
      return average >= 236 && maximum - minimum <= 30;
    }

    function add(index) {
      if (index < 0 || index >= count || visited[index] || !isBackground(index)) return;
      visited[index] = 1;
      queue[tail++] = index;
    }

    var x;
    var y;
    for (x = 0; x < width; x += 1) {
      add(x);
      add((height - 1) * width + x);
    }
    for (y = 0; y < height; y += 1) {
      add(y * width);
      add(y * width + width - 1);
    }

    while (head < tail) {
      var index = queue[head++];
      var px = index % width;
      var py = (index / width) | 0;
      if (px > 0) add(index - 1);
      if (px < width - 1) add(index + 1);
      if (py > 0) add(index - width);
      if (py < height - 1) add(index + width);
    }

    for (var i = 0; i < count; i += 1) {
      if (!visited[i]) continue;
      var offset = i * 4;
      var brightness = (data[offset] + data[offset + 1] + data[offset + 2]) / 3;
      var originalAlpha = data[offset + 3];
      var remainingAlpha = clamp((248 - brightness) / 12, 0, 1);
      data[offset + 3] = Math.round(originalAlpha * remainingAlpha);
    }

    context.putImageData(imageData, 0, 0);
    return canvas;
  }

  function initialiseViewer(viewer) {
    if (!viewer || viewer.dataset.initialised === 'true') return;
    viewer.dataset.initialised = 'true';

    var section = viewer.closest('.shopify-section') || document;
    var openButtons = section.querySelectorAll('[data-lux-room-open]');
    var closeButtons = viewer.querySelectorAll('[data-lux-room-close]');
    var dialog = viewer.querySelector('[data-lux-room-dialog]');
    var workspace = viewer.querySelector('[data-lux-room-workspace]');
    var emptyState = viewer.querySelector('[data-lux-room-empty]');
    var canvasWrap = viewer.querySelector('[data-lux-room-canvas-wrap]');
    var canvas = viewer.querySelector('[data-lux-room-canvas]');
    var context = canvas.getContext('2d');
    var fileInputs = viewer.querySelectorAll('[data-lux-room-input]');
    var searchInput = viewer.querySelector('[data-lux-room-search]');
    var libraryEmpty = viewer.querySelector('[data-lux-room-library-empty]');
    var catalogCards = Array.prototype.slice.call(viewer.querySelectorAll('[data-lux-room-catalog-item]'));
    var sizeInput = viewer.querySelector('[data-lux-room-size]');
    var sizeOutput = viewer.querySelector('[data-lux-room-size-output]');
    var rotationInput = viewer.querySelector('[data-lux-room-rotation]');
    var rotationOutput = viewer.querySelector('[data-lux-room-rotation-output]');
    var selectedEmpty = viewer.querySelector('[data-lux-room-selected-empty]');
    var selectedContent = viewer.querySelector('[data-lux-room-selected-content]');
    var selectedImage = viewer.querySelector('[data-lux-room-selected-image]');
    var selectedTitle = viewer.querySelector('[data-lux-room-selected-title]');
    var selectedPrice = viewer.querySelector('[data-lux-room-selected-price]');
    var dimensionNote = viewer.querySelector('[data-lux-room-dimension-note]');
    var wallWidthInput = viewer.querySelector('[data-lux-room-wall-width]');
    var itemCount = viewer.querySelector('[data-lux-room-item-count]');
    var totalPrice = viewer.querySelector('[data-lux-room-total]');
    var status = viewer.querySelector('[data-lux-room-status]');
    var beforeButton = viewer.querySelector('[data-lux-room-before]');
    var beforeLabel = viewer.querySelector('[data-lux-room-before-label]');
    var undoButton = viewer.querySelector('[data-lux-room-undo]');
    var redoButton = viewer.querySelector('[data-lux-room-redo]');
    var cartButton = viewer.querySelector('[data-lux-room-add-cart]');
    var downloadButton = viewer.querySelector('[data-lux-room-download]');
    var shareButton = viewer.querySelector('[data-lux-room-share]');
    var mobileTabs = Array.prototype.slice.call(viewer.querySelectorAll('[data-lux-room-mobile-tab]'));
    var mobileCount = viewer.querySelector('[data-lux-room-mobile-count]');
    var aiPanel = viewer.querySelector('[data-lux-room-ai-panel]');
    var aiConsent = viewer.querySelector('[data-lux-room-ai-consent]');
    var aiStyle = viewer.querySelector('[data-lux-room-ai-style]');
    var aiAnalyzeButton = viewer.querySelector('[data-lux-room-ai-analyze]');
    var aiEnhanceButton = viewer.querySelector('[data-lux-room-ai-enhance]');
    var aiProgress = viewer.querySelector('[data-lux-room-ai-progress]');
    var aiProgressText = viewer.querySelector('[data-lux-room-ai-progress-text]');
    var aiInsight = viewer.querySelector('[data-lux-room-ai-insight]');
    var aiRoomType = viewer.querySelector('[data-lux-room-ai-room-type]');
    var aiLighting = viewer.querySelector('[data-lux-room-ai-lighting]');
    var aiTips = viewer.querySelector('[data-lux-room-ai-tips]');
    var aiResult = viewer.querySelector('[data-lux-room-ai-result]');
    var aiViewButtons = Array.prototype.slice.call(viewer.querySelectorAll('[data-lux-room-ai-view]'));
    var flipButton = viewer.querySelector('[data-lux-room-flip]');
    var shadowButton = viewer.querySelector('[data-lux-room-shadow]');
    var cleanButton = viewer.querySelector('[data-lux-room-clean]');
    var lockButton = viewer.querySelector('[data-lux-room-lock]');
    var backButton = viewer.querySelector('[data-lux-room-back]');
    var forwardButton = viewer.querySelector('[data-lux-room-forward]');
    var duplicateButton = viewer.querySelector('[data-lux-room-duplicate]');
    var removeButton = viewer.querySelector('[data-lux-room-remove]');
    var resetSelectedButton = viewer.querySelector('[data-lux-room-reset-selected]');
    var realSizeButton = viewer.querySelector('[data-lux-room-real-size]');
    var clearButton = viewer.querySelector('[data-lux-room-clear]');
    var previousFocus = null;
    var baseImageCache = {};
    var drawableCache = {};

    var products = {};
    var currentProductKey = '';
    catalogCards.forEach(function (card) {
      var product = {
        key: card.dataset.catalogId,
        title: card.dataset.productTitle,
        handle: card.dataset.productHandle,
        image: card.dataset.productImage,
        thumbnail: card.dataset.productThumbnail,
        imageFallback: card.dataset.productImageFallback === 'true',
        url: card.dataset.productUrl,
        variantId: card.dataset.variantId,
        priceCents: Number(card.dataset.priceCents) || 0,
        priceDisplay: card.dataset.priceDisplay || '',
        available: card.dataset.productAvailable === 'true',
        dimensions: card.dataset.productDimensions || '',
        widthCm: Number(card.dataset.productWidthCm) || 0,
        card: card
      };
      products[product.key] = product;
      if (card.dataset.currentProduct === 'true') currentProductKey = product.key;
    });

    var state = {
      room: null,
      items: [],
      selectedId: null,
      nextId: 1,
      showBefore: false,
      dragging: false,
      dragMoved: false,
      dragOffsetX: 0,
      dragOffsetY: 0,
      history: [],
      historyIndex: -1,
      aiAnalysis: null,
      aiImage: null,
      aiBusy: false,
      showAi: false
    };

    function setStatus(message, error) {
      status.textContent = message || '';
      status.classList.toggle('is-error', Boolean(error));
    }

    function selectedItem() {
      return state.items.find(function (item) { return item.id === state.selectedId; }) || null;
    }

    function setMobilePanel(panel) {
      var allowed = ['products', 'edit'];
      if (aiPanel) allowed.push('ai');
      var nextPanel = allowed.indexOf(panel) !== -1 ? panel : 'products';
      viewer.dataset.mobilePanel = nextPanel;
      mobileTabs.forEach(function (button) {
        button.setAttribute('aria-selected', String(button.dataset.luxRoomMobileTab === nextPanel));
      });
    }

    function parseProductWidth(product) {
      if (!product) return 0;
      if (product.widthCm > 0) return product.widthCm;
      var raw = String(product.dimensions || '').replace(/,/g, '.');
      var patterns = [
        /(?:width|wide|\bw\b)\s*[:=]?\s*(\d+(?:\.\d+)?)\s*cm/i,
        /\bw\s*(\d+(?:\.\d+)?)\s*cm/i,
        /(?:dimensions?\s*[:=]?\s*)?(\d+(?:\.\d+)?)\s*(?:cm\s*)?[x×]\s*\d/i
      ];
      for (var i = 0; i < patterns.length; i += 1) {
        var match = raw.match(patterns[i]);
        if (match) return Number(match[1]) || 0;
      }
      return 0;
    }

    function currency(value) {
      try {
        return new Intl.NumberFormat('en-MT', {
          style: 'currency',
          currency: viewer.dataset.currency || 'EUR'
        }).format(value / 100);
      } catch (error) {
        return '€' + (value / 100).toFixed(2);
      }
    }

    function itemMetrics(item) {
      if (!item || !item.drawable || !canvas.width) return null;
      var dimensions = imageDimensions(item.drawable);
      var width = canvas.width * item.size / 100;
      return {
        width: width,
        height: width * dimensions.height / dimensions.width,
        centreX: item.x * canvas.width,
        centreY: item.y * canvas.height
      };
    }

    function drawSelection(item, metrics) {
      context.save();
      context.translate(metrics.centreX, metrics.centreY);
      context.rotate(item.rotation * Math.PI / 180);
      context.lineWidth = Math.max(2, canvas.width * .0015);
      context.strokeStyle = item.locked ? 'rgba(90,70,56,.72)' : 'rgba(255,255,255,.95)';
      context.setLineDash([Math.max(6, canvas.width * .006), Math.max(4, canvas.width * .004)]);
      context.strokeRect(-metrics.width / 2, -metrics.height / 2, metrics.width, metrics.height);
      context.setLineDash([]);
      context.fillStyle = item.locked ? '#5a4638' : '#fff';
      context.strokeStyle = '#5a4638';
      context.lineWidth = Math.max(1, canvas.width * .001);
      var handle = Math.max(6, canvas.width * .006);
      [[-1, -1], [1, -1], [-1, 1], [1, 1]].forEach(function (point) {
        context.beginPath();
        context.arc(point[0] * metrics.width / 2, point[1] * metrics.height / 2, handle, 0, Math.PI * 2);
        context.fill();
        context.stroke();
      });
      context.restore();
    }

    function drawBranding() {
      var fontSize = Math.max(12, Math.round(canvas.width * .013));
      var padding = Math.max(12, Math.round(fontSize * .8));
      var label = 'LUXONAS HOME  ·  ROOM VISUAL';
      context.save();
      context.font = '600 ' + fontSize + 'px Arial, sans-serif';
      var labelWidth = context.measureText(label).width;
      context.fillStyle = 'rgba(255,255,255,.88)';
      context.fillRect(padding, canvas.height - fontSize - padding * 2, labelWidth + padding * 2, fontSize + padding * 1.5);
      context.fillStyle = '#2a231e';
      context.textBaseline = 'middle';
      context.fillText(label, padding * 2, canvas.height - fontSize / 2 - padding * 1.25);
      context.restore();
    }

    function draw(exporting, includeBranding) {
      context.clearRect(0, 0, canvas.width, canvas.height);
      if (!state.room) return;
      context.drawImage(state.room, 0, 0, canvas.width, canvas.height);

      if (!state.showBefore || exporting) {
        if (state.showAi && state.aiImage) {
          context.drawImage(state.aiImage, 0, 0, canvas.width, canvas.height);
        } else {
          state.items.forEach(function (item) {
            var metrics = itemMetrics(item);
            if (!metrics) return;
            context.save();
            context.translate(metrics.centreX, metrics.centreY);
            context.rotate(item.rotation * Math.PI / 180);
            context.scale(item.flipped ? -1 : 1, 1);
            if (item.shadow) {
              context.shadowColor = 'rgba(22,17,13,.24)';
              context.shadowBlur = Math.max(8, canvas.width * .012);
              context.shadowOffsetX = Math.max(2, canvas.width * .004);
              context.shadowOffsetY = Math.max(4, canvas.width * .008);
            }
            context.drawImage(item.drawable, -metrics.width / 2, -metrics.height / 2, metrics.width, metrics.height);
            context.restore();
          });

          if (!exporting) {
            var selected = selectedItem();
            var selectedMetrics = itemMetrics(selected);
            if (selected && selectedMetrics) drawSelection(selected, selectedMetrics);
          }
        }
      }

      if (exporting && includeBranding !== false) drawBranding();
    }

    function loadBaseImage(product) {
      if (baseImageCache[product.key]) return baseImageCache[product.key];
      baseImageCache[product.key] = loadImage(product.image, true).then(function (image) {
        return { image: image, safe: true };
      }).catch(function () {
        return loadImage(product.image, false).then(function (image) {
          return { image: image, safe: false };
        });
      });
      return baseImageCache[product.key];
    }

    function loadDrawable(product, clean) {
      var cacheKey = product.key + '|' + (clean ? 'clean' : 'original');
      if (drawableCache[cacheKey]) return drawableCache[cacheKey];
      drawableCache[cacheKey] = loadBaseImage(product).then(function (base) {
        if (clean && base.safe) {
          try {
            return { drawable: cleanConnectedWhiteBackground(base.image), safe: true };
          } catch (error) {
            return { drawable: base.image, safe: base.safe, cleanFailed: true };
          }
        }
        return { drawable: base.image, safe: base.safe };
      });
      return drawableCache[cacheKey];
    }

    function hydrateItem(item) {
      if (!item || !item.product) return;
      item.loadVersion = (item.loadVersion || 0) + 1;
      var version = item.loadVersion;
      item.loading = true;
      loadDrawable(item.product, item.clean).then(function (result) {
        if (item.loadVersion !== version) return;
        item.drawable = result.drawable;
        item.safe = result.safe;
        item.loading = false;
        if (result.cleanFailed) item.clean = false;
        updateInspector();
        updateSummary();
        draw(false);
      }).catch(function () {
        if (item.loadVersion !== version) return;
        item.loading = false;
        item.safe = false;
        setStatus('One product image could not be loaded.', true);
        updateSummary();
      });
    }

    function serialisableItem(item) {
      return {
        id: item.id,
        productKey: item.product.key,
        x: item.x,
        y: item.y,
        size: item.size,
        rotation: item.rotation,
        flipped: item.flipped,
        shadow: item.shadow,
        clean: item.clean,
        locked: item.locked
      };
    }

    function snapshot() {
      return JSON.stringify({
        items: state.items.map(serialisableItem),
        selectedId: state.selectedId
      });
    }

    function updateHistoryButtons() {
      undoButton.disabled = state.historyIndex <= 0;
      redoButton.disabled = state.historyIndex < 0 || state.historyIndex >= state.history.length - 1;
    }

    function commitHistory() {
      var value = snapshot();
      if (state.history[state.historyIndex] === value) return;
      invalidateAiResult(true);
      state.history = state.history.slice(0, state.historyIndex + 1);
      state.history.push(value);
      if (state.history.length > HISTORY_LIMIT) state.history.shift();
      state.historyIndex = state.history.length - 1;
      updateHistoryButtons();
    }

    function restoreSnapshot(value) {
      var data = JSON.parse(value);
      invalidateAiResult(true);
      state.items = data.items.map(function (saved) {
        var item = {
          id: saved.id,
          product: products[saved.productKey],
          x: saved.x,
          y: saved.y,
          size: saved.size,
          rotation: saved.rotation,
          flipped: saved.flipped,
          shadow: saved.shadow,
          clean: saved.clean,
          locked: saved.locked,
          drawable: null,
          safe: true,
          loading: true
        };
        hydrateItem(item);
        return item;
      }).filter(function (item) { return Boolean(item.product); });
      state.nextId = state.items.reduce(function (maximum, item) { return Math.max(maximum, item.id + 1); }, 1);
      state.selectedId = state.items.some(function (item) { return item.id === data.selectedId; }) ? data.selectedId : (state.items.length ? state.items[state.items.length - 1].id : null);
      updateInspector();
      updateSummary();
      draw(false);
    }

    function undo() {
      if (state.historyIndex <= 0) return;
      state.historyIndex -= 1;
      restoreSnapshot(state.history[state.historyIndex]);
      updateHistoryButtons();
      setStatus('Last change undone.');
    }

    function redo() {
      if (state.historyIndex >= state.history.length - 1) return;
      state.historyIndex += 1;
      restoreSnapshot(state.history[state.historyIndex]);
      updateHistoryButtons();
      setStatus('Change restored.');
    }

    function addProduct(product, commit) {
      if (!product) return;
      if (state.items.length >= MAX_ROOM_ITEMS) {
        setStatus('You can place up to ' + MAX_ROOM_ITEMS + ' products in one room.', true);
        return;
      }
      var offset = Math.min(state.items.length * .025, .14);
      var item = {
        id: state.nextId++,
        product: product,
        x: clamp(.5 + offset, .1, .9),
        y: clamp(.55 + offset, .1, .9),
        size: 38,
        rotation: 0,
        flipped: false,
        shadow: true,
        clean: viewer.dataset.autoClean === 'true' && product.imageFallback,
        locked: false,
        drawable: null,
        safe: true,
        loading: true
      };
      state.items.push(item);
      state.selectedId = item.id;
      hydrateItem(item);
      updateInspector();
      updateSummary();
      draw(false);
      if (commit !== false) commitHistory();
      setStatus(product.title + ' added to your room.');
    }

    function removeSelected() {
      var selected = selectedItem();
      if (!selected) return;
      state.items = state.items.filter(function (item) { return item.id !== selected.id; });
      state.selectedId = state.items.length ? state.items[state.items.length - 1].id : null;
      updateInspector();
      updateSummary();
      draw(false);
      commitHistory();
      setStatus(selected.product.title + ' removed.');
    }

    function duplicateSelected() {
      var selected = selectedItem();
      if (!selected || state.items.length >= MAX_ROOM_ITEMS) {
        if (state.items.length >= MAX_ROOM_ITEMS) setStatus('Maximum room item limit reached.', true);
        return;
      }
      var copy = {
        id: state.nextId++,
        product: selected.product,
        x: clamp(selected.x + .04, -.1, 1.1),
        y: clamp(selected.y + .04, -.1, 1.1),
        size: selected.size,
        rotation: selected.rotation,
        flipped: selected.flipped,
        shadow: selected.shadow,
        clean: selected.clean,
        locked: false,
        drawable: selected.drawable,
        safe: selected.safe,
        loading: selected.loading
      };
      state.items.push(copy);
      state.selectedId = copy.id;
      if (!copy.drawable) hydrateItem(copy);
      updateInspector();
      updateSummary();
      draw(false);
      commitHistory();
      setStatus(selected.product.title + ' duplicated.');
    }

    function selectItem(id) {
      state.selectedId = id;
      updateInspector();
      draw(false);
    }

    function moveLayer(direction) {
      var selected = selectedItem();
      if (!selected) return;
      var index = state.items.indexOf(selected);
      var target = clamp(index + direction, 0, state.items.length - 1);
      if (target === index) return;
      state.items.splice(index, 1);
      state.items.splice(target, 0, selected);
      updateInspector();
      draw(false);
      commitHistory();
    }

    function updateInspector() {
      var selected = selectedItem();
      selectedEmpty.hidden = Boolean(selected);
      selectedContent.hidden = !selected;
      canvas.classList.toggle('has-selection', Boolean(selected && !selected.locked));
      if (!selected) return;

      selectedImage.src = selected.product.thumbnail;
      selectedTitle.textContent = selected.product.title;
      selectedPrice.textContent = selected.product.priceDisplay + (selected.product.available ? '' : ' · Visualise only');
      sizeInput.value = String(Math.round(selected.size));
      sizeOutput.textContent = Math.round(selected.size) + '%';
      rotationInput.value = String(Math.round(selected.rotation));
      rotationOutput.textContent = Math.round(selected.rotation) + '°';
      flipButton.setAttribute('aria-pressed', String(selected.flipped));
      shadowButton.setAttribute('aria-pressed', String(selected.shadow));
      cleanButton.setAttribute('aria-pressed', String(selected.clean));
      lockButton.setAttribute('aria-pressed', String(selected.locked));

      sizeInput.disabled = selected.locked;
      rotationInput.disabled = selected.locked;
      flipButton.disabled = selected.locked;
      cleanButton.disabled = selected.locked || selected.safe === false;
      resetSelectedButton.disabled = selected.locked;
      backButton.disabled = state.items.indexOf(selected) === 0;
      forwardButton.disabled = state.items.indexOf(selected) === state.items.length - 1;

      var productWidth = parseProductWidth(selected.product);
      if (productWidth > 0) {
        dimensionNote.textContent = 'Saved product width detected: approximately ' + productWidth + ' cm.';
        realSizeButton.disabled = false;
      } else {
        dimensionNote.textContent = 'No product width was found. Add custom.width_cm to enable approximate scaling.';
        realSizeButton.disabled = true;
      }
    }

    function updateCatalogCounts() {
      var counts = {};
      state.items.forEach(function (item) {
        counts[item.product.key] = (counts[item.product.key] || 0) + 1;
      });
      catalogCards.forEach(function (card) {
        var count = counts[card.dataset.catalogId] || 0;
        var badge = card.querySelector('[data-lux-room-catalog-count]');
        if (!badge) return;
        badge.hidden = count === 0;
        badge.textContent = String(count);
      });
    }

    function updateSummary() {
      var total = 0;
      var availableCount = 0;
      var canvasSafe = true;
      state.items.forEach(function (item) {
        if (item.product.available && item.product.variantId) {
          total += item.product.priceCents;
          availableCount += 1;
        }
        if (item.safe === false || item.loading) canvasSafe = false;
      });
      itemCount.textContent = state.items.length + (state.items.length === 1 ? ' item' : ' items');
      if (mobileCount) mobileCount.textContent = String(state.items.length);
      totalPrice.textContent = currency(total);
      cartButton.disabled = availableCount === 0;
      downloadButton.disabled = !state.room || state.items.length === 0 || !canvasSafe;
      shareButton.disabled = !state.room || state.items.length === 0 || !canvasSafe;
      updateCatalogCounts();
      updateAiPanel();
    }

    function updateAiPanel() {
      if (!aiPanel) return;
      var consented = Boolean(aiConsent && aiConsent.checked);
      var canvasSafe = state.items.every(function (item) {
        return item.safe !== false && !item.loading;
      });
      aiAnalyzeButton.disabled = state.aiBusy || !state.room || !consented;
      aiEnhanceButton.disabled = state.aiBusy || !state.room || !state.items.length || !canvasSafe || !consented;
      aiProgress.hidden = !state.aiBusy;
      aiInsight.hidden = !state.aiAnalysis;
      aiResult.hidden = !state.aiImage;
      aiViewButtons.forEach(function (button) {
        var enhanced = button.dataset.luxRoomAiView === 'enhanced';
        button.setAttribute('aria-pressed', String(enhanced === state.showAi));
        button.disabled = enhanced && !state.aiImage;
      });
    }

    function invalidateAiResult(preserveAnalysis) {
      if (!aiPanel) return;
      var hadResult = Boolean(state.aiImage || state.showAi);
      state.aiImage = null;
      state.showAi = false;
      if (!preserveAnalysis) state.aiAnalysis = null;
      updateAiPanel();
      if (hadResult && state.room) draw(false);
    }

    function setAiView(enhanced) {
      if (enhanced && !state.aiImage) return;
      state.showAi = Boolean(enhanced);
      state.showBefore = false;
      beforeButton.setAttribute('aria-pressed', 'false');
      beforeLabel.hidden = true;
      updateAiPanel();
      draw(false);
    }

    function setAiBusy(busy, message) {
      state.aiBusy = Boolean(busy);
      if (aiProgressText && message) aiProgressText.textContent = message;
      updateAiPanel();
    }

    function aiEndpoint(action) {
      var root = String(viewer.dataset.aiEndpoint || '/apps/luxonas-room-ai').replace(/\/+$/, '');
      return root + '/' + action;
    }

    function roomImageData() {
      if (!state.room || typeof state.room.toDataURL !== 'function') return '';
      return state.room.toDataURL('image/jpeg', .88);
    }

    function compositeImageData() {
      var wasAi = state.showAi;
      state.showAi = false;
      draw(true, false);
      var data = canvas.toDataURL('image/jpeg', .9);
      state.showAi = wasAi;
      draw(false);
      return data;
    }

    function aiRequest(action, payload) {
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = window.setTimeout(function () {
        if (controller) controller.abort();
      }, 120000);
      return fetch(aiEndpoint(action), {
        method: 'POST',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
        signal: controller ? controller.signal : undefined
      }).then(function (response) {
        return response.json().catch(function () { return {}; }).then(function (data) {
          if (!response.ok || !data.ok) {
            var message = data && data.error && data.error.message;
            throw new Error(message || 'The AI service could not complete this request.');
          }
          return data;
        });
      }).finally(function () {
        window.clearTimeout(timer);
      });
    }

    function showRoomAnalysis(analysis) {
      if (!aiPanel || !analysis) return;
      state.aiAnalysis = analysis;
      aiRoomType.textContent = analysis.roomType || 'Room analysed';
      aiLighting.textContent = [analysis.style, analysis.lighting].filter(Boolean).join(' · ');
      aiTips.innerHTML = '';
      (analysis.placementTips || []).slice(0, 4).forEach(function (tip) {
        var item = document.createElement('li');
        item.textContent = tip;
        aiTips.appendChild(item);
      });
      updateAiPanel();
    }

    function analyzeRoomWithAi() {
      if (!aiPanel || !state.room || !aiConsent.checked || state.aiBusy) return;
      setAiBusy(true, 'Scanning room structure and light…');
      setStatus('AI is scanning your room…');
      aiRequest('analyze', {
        consent: true,
        image: roomImageData()
      }).then(function (result) {
        showRoomAnalysis(result.analysis);
        setStatus('Room scan ready. Review the placement guidance below.');
      }).catch(function (error) {
        setStatus(error.message || 'The room scan could not be completed.', true);
      }).finally(function () {
        setAiBusy(false);
      });
    }

    function absoluteImageUrl(value) {
      try {
        return new URL(value, window.location.origin).href;
      } catch (error) {
        return '';
      }
    }

    function enhanceRoomWithAi() {
      if (!aiPanel || !state.room || !state.items.length || !aiConsent.checked || state.aiBusy) return;
      setAiBusy(true, 'Blending products, light and shadows…');
      setStatus('AI is enhancing your Luxonas room. This can take up to a minute.');
      var productImages = [];
      var productNames = [];
      state.items.forEach(function (item) {
        var image = absoluteImageUrl(item.product.image);
        if (image && productImages.indexOf(image) === -1 && productImages.length < 4) productImages.push(image);
        if (productNames.indexOf(item.product.title) === -1) productNames.push(item.product.title);
      });
      aiRequest('enhance', {
        consent: true,
        image: compositeImageData(),
        width: canvas.width,
        height: canvas.height,
        style: aiStyle.value,
        products: productNames,
        productImages: productImages,
        analysis: state.aiAnalysis
      }).then(function (result) {
        return loadImage(result.image, false);
      }).then(function (image) {
        state.aiImage = image;
        state.showAi = true;
        state.showBefore = false;
        beforeButton.setAttribute('aria-pressed', 'false');
        beforeLabel.hidden = true;
        setMobilePanel('ai');
        updateAiPanel();
        draw(false);
        setStatus('AI-enhanced room ready. Compare it with your manual layout.');
      }).catch(function (error) {
        setStatus(error.message || 'The AI-enhanced room could not be created.', true);
      }).finally(function () {
        setAiBusy(false);
      });
    }

    function useRoomFile(file) {
      if (!file) return;
      if (!/^image\/(jpeg|png|webp|heic|heif)$/i.test(file.type) && !/\.(jpe?g|png|webp|heic|heif)$/i.test(file.name)) {
        setStatus('Please choose a JPG, PNG, WebP or HEIC room photograph.', true);
        return;
      }
      if (file.size > MAX_ROOM_BYTES) {
        setStatus('Please choose a room photograph smaller than 20 MB.', true);
        return;
      }

      setStatus('Preparing your room photograph…');
      var objectUrl = URL.createObjectURL(file);
      loadImage(objectUrl, false).then(function (image) {
        URL.revokeObjectURL(objectUrl);
        invalidateAiResult(false);
        state.room = scaledCanvasForImage(image, MAX_ROOM_EDGE);
        canvas.width = state.room.width;
        canvas.height = state.room.height;
        emptyState.hidden = true;
        canvasWrap.classList.add('is-ready');
        draw(false);
        updateSummary();
        setStatus('Drag products to position them, or add more pieces from the collection.');
      }).catch(function () {
        URL.revokeObjectURL(objectUrl);
        setStatus('That photograph could not be opened. Please try a JPG, PNG or WebP image.', true);
      });
    }

    function canvasPoint(event) {
      var rectangle = canvas.getBoundingClientRect();
      return {
        x: (event.clientX - rectangle.left) * canvas.width / rectangle.width,
        y: (event.clientY - rectangle.top) * canvas.height / rectangle.height
      };
    }

    function pointHitsItem(point, item) {
      var metrics = itemMetrics(item);
      if (!metrics) return false;
      var dx = point.x - metrics.centreX;
      var dy = point.y - metrics.centreY;
      var radians = -item.rotation * Math.PI / 180;
      var localX = dx * Math.cos(radians) - dy * Math.sin(radians);
      var localY = dx * Math.sin(radians) + dy * Math.cos(radians);
      return Math.abs(localX) <= metrics.width / 2 && Math.abs(localY) <= metrics.height / 2;
    }

    function topItemAtPoint(point) {
      for (var i = state.items.length - 1; i >= 0; i -= 1) {
        if (pointHitsItem(point, state.items[i])) return state.items[i];
      }
      return null;
    }

    function startDrag(event) {
      if (!state.room || state.showBefore) return;
      if (state.showAi) setAiView(false);
      var point = canvasPoint(event);
      var item = topItemAtPoint(point);
      if (!item) {
        selectItem(null);
        return;
      }
      selectItem(item.id);
      setMobilePanel('edit');
      if (item.locked) {
        setStatus('This product is locked. Unlock it to move it.');
        return;
      }
      state.dragging = true;
      state.dragMoved = false;
      state.dragOffsetX = point.x - item.x * canvas.width;
      state.dragOffsetY = point.y - item.y * canvas.height;
      canvas.classList.add('is-dragging');
      if (canvas.setPointerCapture) canvas.setPointerCapture(event.pointerId);
      event.preventDefault();
    }

    function moveDrag(event) {
      if (!state.dragging) return;
      var item = selectedItem();
      if (!item) return;
      var point = canvasPoint(event);
      var metrics = itemMetrics(item);
      var marginX = metrics ? Math.min(metrics.width * .22, canvas.width * .15) : 0;
      var marginY = metrics ? Math.min(metrics.height * .22, canvas.height * .15) : 0;
      item.x = clamp((point.x - state.dragOffsetX) / canvas.width, -marginX / canvas.width, 1 + marginX / canvas.width);
      item.y = clamp((point.y - state.dragOffsetY) / canvas.height, -marginY / canvas.height, 1 + marginY / canvas.height);
      state.dragMoved = true;
      draw(false);
      event.preventDefault();
    }

    function endDrag(event) {
      if (!state.dragging) return;
      state.dragging = false;
      canvas.classList.remove('is-dragging');
      if (event && canvas.hasPointerCapture && canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      if (state.dragMoved) commitHistory();
    }

    function resetSelected() {
      var selected = selectedItem();
      if (!selected || selected.locked) return;
      selected.x = .5;
      selected.y = .55;
      selected.size = 38;
      selected.rotation = 0;
      selected.flipped = false;
      selected.shadow = true;
      updateInspector();
      draw(false);
      commitHistory();
      setStatus('Selected product placement reset.');
    }

    function applyRealSize() {
      var selected = selectedItem();
      if (!selected) return;
      var productWidth = parseProductWidth(selected.product);
      var wallWidth = Number(wallWidthInput.value);
      if (!(productWidth > 0) || !(wallWidth > 0)) {
        setStatus('Add a valid wall width and product width before applying scale.', true);
        return;
      }
      selected.size = clamp(productWidth / wallWidth * 100, 5, 140);
      updateInspector();
      draw(false);
      commitHistory();
      setStatus('Approximate scale applied using ' + productWidth + ' cm product width and ' + wallWidth + ' cm visible room width.');
    }

    function clearDesign() {
      state.items = [];
      state.selectedId = null;
      state.nextId = 1;
      if (products[currentProductKey]) addProduct(products[currentProductKey], false);
      state.history = [];
      state.historyIndex = -1;
      commitHistory();
      updateInspector();
      updateSummary();
      draw(false);
      setStatus('Design cleared. The current product is ready to place again.');
    }

    function exportBlob() {
      return new Promise(function (resolve, reject) {
        if (!state.room) {
          reject(new Error('Upload a room photograph first.'));
          return;
        }
        draw(true);
        try {
          canvas.toBlob(function (blob) {
            draw(false);
            if (blob) resolve(blob);
            else reject(new Error('The room visual could not be created.'));
          }, 'image/png');
        } catch (error) {
          draw(false);
          reject(error);
        }
      });
    }

    function downloadBlob(blob) {
      var link = document.createElement('a');
      var downloadUrl = URL.createObjectURL(blob);
      link.href = downloadUrl;
      link.download = 'luxonas-' + (viewer.dataset.productHandle || 'room') + '-room-design.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(function () { URL.revokeObjectURL(downloadUrl); }, 1000);
    }

    function downloadDesign() {
      setStatus('Preparing your room visual…');
      exportBlob().then(function (blob) {
        downloadBlob(blob);
        setStatus('Your room visual has been downloaded.');
      }).catch(function () {
        setStatus('This visual cannot be downloaded until all product images finish loading.', true);
      });
    }

    function designText() {
      var lines = ['My Luxonas room design:', ''];
      var counts = {};
      state.items.forEach(function (item) {
        counts[item.product.key] = (counts[item.product.key] || 0) + 1;
      });
      Object.keys(counts).forEach(function (key) {
        var product = products[key];
        lines.push(counts[key] + ' × ' + product.title + ' — ' + window.location.origin + product.url);
      });
      lines.push('', 'Created at Luxonas.com');
      return lines.join('\n');
    }

    function shareDesign() {
      setStatus('Preparing your room design to share…');
      exportBlob().then(function (blob) {
        var file;
        try {
          file = new File([blob], 'luxonas-room-design.png', { type: 'image/png' });
        } catch (error) {
          file = null;
        }
        var shareData = {
          title: 'My Luxonas room design',
          text: designText()
        };
        if (file) shareData.files = [file];
        if (navigator.share && (!shareData.files || !navigator.canShare || navigator.canShare(shareData))) {
          return navigator.share(shareData).then(function () {
            setStatus('Room design shared.');
          });
        }
        downloadBlob(blob);
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(designText()).catch(function () {});
        }
        var shareLink = document.createElement('a');
        shareLink.href = 'https://wa.me/?text=' + encodeURIComponent(designText());
        shareLink.target = '_blank';
        shareLink.rel = 'noopener';
        document.body.appendChild(shareLink);
        shareLink.click();
        shareLink.remove();
        setStatus('The visual was downloaded and the product list opened for sharing.');
      }).catch(function () {
        setStatus('This visual cannot be shared until all product images finish loading.', true);
      });
    }

    function addRoomToCart() {
      var grouped = {};
      state.items.forEach(function (item) {
        if (!item.product.available || !item.product.variantId) return;
        grouped[item.product.variantId] = (grouped[item.product.variantId] || 0) + 1;
      });
      var items = Object.keys(grouped).map(function (id) {
        return { id: Number(id), quantity: grouped[id] };
      });
      if (!items.length) {
        setStatus('None of the visualised products are currently available to add.', true);
        return;
      }

      cartButton.disabled = true;
      cartButton.textContent = 'Adding…';
      fetch(viewer.dataset.cartAddUrl || '/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (response) {
        if (!response.ok) throw new Error('Cart request failed.');
        return response.json();
      }).then(function () {
        cartButton.textContent = 'Added to cart';
        setStatus(items.length + (items.length === 1 ? ' product' : ' products') + ' added. Opening your cart…');
        window.setTimeout(function () {
          window.location.href = viewer.dataset.cartUrl || '/cart';
        }, 650);
      }).catch(function () {
        cartButton.disabled = false;
        cartButton.textContent = 'Add room to cart';
        setStatus('The room could not be added. One product may require a different option or pre-order selection.', true);
      });
    }

    function openViewer(event) {
      if (event) event.preventDefault();
      previousFocus = document.activeElement;
      if (!state.items.length && products[currentProductKey]) {
        addProduct(products[currentProductKey], false);
        commitHistory();
      }
      setMobilePanel('products');
      viewer.hidden = false;
      document.body.classList.add('lux-room-open');
      window.requestAnimationFrame(function () {
        viewer.classList.add('is-open');
        var close = viewer.querySelector('.lux-room-close');
        if (close) close.focus();
      });
    }

    function closeViewer() {
      viewer.classList.remove('is-open');
      document.body.classList.remove('lux-room-open');
      window.setTimeout(function () {
        viewer.hidden = true;
        if (previousFocus && typeof previousFocus.focus === 'function') previousFocus.focus();
      }, 220);
    }

    openButtons.forEach(function (button) { button.addEventListener('click', openViewer); });
    closeButtons.forEach(function (button) { button.addEventListener('click', closeViewer); });
    fileInputs.forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.files && input.files[0]) useRoomFile(input.files[0]);
        input.value = '';
      });
    });
    catalogCards.forEach(function (card) {
      card.addEventListener('click', function () {
        addProduct(products[card.dataset.catalogId], true);
      });
    });
    mobileTabs.forEach(function (button) {
      button.addEventListener('click', function () {
        setMobilePanel(button.dataset.luxRoomMobileTab);
        if (button.dataset.luxRoomMobileTab === 'edit' && !selectedItem()) {
          setStatus('Select a product in the room to edit it.');
        } else if (button.dataset.luxRoomMobileTab === 'ai' && !state.room) {
          setStatus('Upload a room photograph to use AI finishing.');
        }
      });
    });
    if (aiPanel) {
      aiConsent.addEventListener('change', updateAiPanel);
      aiAnalyzeButton.addEventListener('click', analyzeRoomWithAi);
      aiEnhanceButton.addEventListener('click', enhanceRoomWithAi);
      aiViewButtons.forEach(function (button) {
        button.addEventListener('click', function () {
          setAiView(button.dataset.luxRoomAiView === 'enhanced');
        });
      });
    }

    searchInput.addEventListener('input', function () {
      var query = searchInput.value.trim().toLowerCase();
      var visible = 0;
      catalogCards.forEach(function (card) {
        var matches = !query || (card.dataset.productTitle || '').toLowerCase().indexOf(query) !== -1;
        card.classList.toggle('is-filtered', !matches);
        if (matches) visible += 1;
      });
      libraryEmpty.hidden = visible !== 0;
    });

    sizeInput.addEventListener('input', function () {
      var selected = selectedItem();
      if (!selected || selected.locked) return;
      selected.size = Number(sizeInput.value);
      sizeOutput.textContent = Math.round(selected.size) + '%';
      draw(false);
    });
    sizeInput.addEventListener('change', commitHistory);
    rotationInput.addEventListener('input', function () {
      var selected = selectedItem();
      if (!selected || selected.locked) return;
      selected.rotation = Number(rotationInput.value);
      rotationOutput.textContent = Math.round(selected.rotation) + '°';
      draw(false);
    });
    rotationInput.addEventListener('change', commitHistory);

    flipButton.addEventListener('click', function () {
      var selected = selectedItem();
      if (!selected || selected.locked) return;
      selected.flipped = !selected.flipped;
      updateInspector();
      draw(false);
      commitHistory();
    });
    shadowButton.addEventListener('click', function () {
      var selected = selectedItem();
      if (!selected) return;
      selected.shadow = !selected.shadow;
      updateInspector();
      draw(false);
      commitHistory();
    });
    cleanButton.addEventListener('click', function () {
      var selected = selectedItem();
      if (!selected || selected.locked || selected.safe === false) return;
      selected.clean = !selected.clean;
      selected.drawable = null;
      hydrateItem(selected);
      updateInspector();
      commitHistory();
      setStatus(selected.clean ? 'Cleaning the selected product background…' : 'Original product image restored.');
    });
    lockButton.addEventListener('click', function () {
      var selected = selectedItem();
      if (!selected) return;
      selected.locked = !selected.locked;
      updateInspector();
      draw(false);
      commitHistory();
    });
    backButton.addEventListener('click', function () { moveLayer(-1); });
    forwardButton.addEventListener('click', function () { moveLayer(1); });
    duplicateButton.addEventListener('click', duplicateSelected);
    removeButton.addEventListener('click', removeSelected);
    resetSelectedButton.addEventListener('click', resetSelected);
    realSizeButton.addEventListener('click', applyRealSize);
    clearButton.addEventListener('click', clearDesign);
    undoButton.addEventListener('click', undo);
    redoButton.addEventListener('click', redo);
    cartButton.addEventListener('click', addRoomToCart);
    downloadButton.addEventListener('click', downloadDesign);
    shareButton.addEventListener('click', shareDesign);
    beforeButton.addEventListener('click', function () {
      state.showBefore = !state.showBefore;
      beforeButton.setAttribute('aria-pressed', String(state.showBefore));
      beforeLabel.hidden = !state.showBefore;
      draw(false);
    });

    canvas.addEventListener('pointerdown', startDrag);
    canvas.addEventListener('pointermove', moveDrag);
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    workspace.addEventListener('dragenter', function (event) {
      event.preventDefault();
      workspace.classList.add('is-dragover');
    });
    workspace.addEventListener('dragover', function (event) {
      event.preventDefault();
      workspace.classList.add('is-dragover');
    });
    workspace.addEventListener('dragleave', function (event) {
      if (!workspace.contains(event.relatedTarget)) workspace.classList.remove('is-dragover');
    });
    workspace.addEventListener('drop', function (event) {
      event.preventDefault();
      workspace.classList.remove('is-dragover');
      if (event.dataTransfer && event.dataTransfer.files[0]) useRoomFile(event.dataTransfer.files[0]);
    });

    viewer.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        closeViewer();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = viewer.querySelectorAll('button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), select:not([disabled]):not([hidden]), summary, [href], [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        last.focus();
        event.preventDefault();
      } else if (!event.shiftKey && document.activeElement === last) {
        first.focus();
        event.preventDefault();
      }
    });

    dialog.addEventListener('click', function (event) { event.stopPropagation(); });
    wallWidthInput.value = viewer.dataset.defaultWallWidth || wallWidthInput.value;
    updateInspector();
    updateSummary();
    updateHistoryButtons();
    updateAiPanel();
    setMobilePanel('products');
  }

  function initialiseAll(context) {
    Array.prototype.forEach.call((context || document).querySelectorAll('[data-lux-room-viewer]'), initialiseViewer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initialiseAll(document); });
  } else {
    initialiseAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initialiseAll(event.target);
  });
})();
